import { useLayoutEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteProvider, useSite } from "./lib/site-context";
import { A11yProvider } from "./lib/a11y-context";
import { getToken } from "./lib/api";
import { Navigate } from "react-router-dom";

import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import MobileActionBar from "./components/layout/MobileActionBar";
import PublicPage from "./pages/PublicPage";
import PraxisCheckPage from "./pages/PraxisCheckPage";
import KontaktPage from "./pages/KontaktPage";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import Dashboard from "./pages/admin/Dashboard";
import PagesList from "./pages/admin/PagesList";
import MediaLibrary from "./pages/admin/MediaLibrary";
import FaqAdmin from "./pages/admin/FaqAdmin";
import GlossaryAdmin from "./pages/admin/GlossaryAdmin";
import NewsAdmin from "./pages/admin/NewsAdmin";
import SubmissionsAdmin from "./pages/admin/SubmissionsAdmin";
import SettingsAdmin from "./pages/admin/SettingsAdmin";
import SeoAdmin from "./pages/admin/SeoAdmin";
import UsersAdmin from "./pages/admin/UsersAdmin";
import TranslationsAdmin from "./pages/admin/TranslationsAdmin";
import PricingAdmin from "./pages/admin/PricingAdmin";
import PressAdmin from "./pages/admin/PressAdmin";
import PreislistePage from "./pages/PreislistePage";
import NewsArticlePage from "./pages/NewsArticlePage";
import NewsIndexPage from "./pages/NewsIndexPage";
import AcademyEventPage from "./pages/AcademyEventPage";
import EventsAdmin from "./pages/admin/EventsAdmin";
import EventsIndexPage from "./pages/EventsIndexPage";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (hash) {
      return;
    }

    const scrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollTop();

    const raf1 = window.requestAnimationFrame(scrollTop);
    const raf2 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollTop);
    });

    const t1 = window.setTimeout(scrollTop, 80);
    const t2 = window.setTimeout(scrollTop, 250);
    const t3 = window.setTimeout(scrollTop, 600);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [pathname, hash]);

  return null;
}

function PublicShell() {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <Header />
      <main><Outlet /></main>
      <Footer />
      <div aria-hidden className="lg:hidden" style={{ height: 76 }} />
      <MobileActionBar />
    </div>
  );
}

function ProtectedAdmin({ children }) {
  if (!getToken()) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <A11yProvider>
        <SiteProvider>
          <ScrollToTop />
          <Toaster position="bottom-right" richColors />
          <Routes>
            {/* Public */}
            <Route element={<PublicShell />}>
              <Route path="/" element={<PublicPage slug="home" />} />
              <Route path="/workflow" element={<PublicPage slug="workflow" />} />
              <Route path="/leistungen" element={<PublicPage slug="leistungen" />} />
              <Route path="/ueber-uns" element={<PublicPage slug="ueber-uns" />} />
              <Route path="/ueber-uns/veranstaltungen" element={<EventsIndexPage />} />
              <Route path="/partner" element={<PublicPage slug="partner" />} />
              <Route path="/presse" element={<PublicPage slug="presse" />} />
              <Route path="/wissen" element={<PublicPage slug="wissen" />} />
              <Route path="/wissen/news" element={<NewsIndexPage />} />
              <Route path="/wissen/news/:slug" element={<NewsArticlePage />} />
              <Route path="/akademie/:slug" element={<AcademyEventPage />} />
              <Route path="/preisliste" element={<PreislistePage />} />
              <Route path="/impressum" element={<PublicPage slug="impressum" />} />
              <Route path="/datenschutz" element={<PublicPage slug="datenschutz" />} />
              <Route path="/praxis-check" element={<PraxisCheckPage />} />
              <Route path="/kontakt" element={<KontaktPage />} />
              <Route path="/seiten/:slug" element={<PublicPage />} />
            </Route>
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
              <Route index element={<Dashboard />} />
              <Route path="pages" element={<PagesList />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="faq" element={<FaqAdmin />} />
              <Route path="glossary" element={<GlossaryAdmin />} />
              <Route path="news" element={<NewsAdmin />} />
              <Route path="events" element={<EventsAdmin />} />
              <Route path="submissions" element={<SubmissionsAdmin />} />
              <Route path="settings" element={<SettingsAdmin />} />
              <Route path="seo" element={<SeoAdmin />} />
              <Route path="users" element={<UsersAdmin />} />
              <Route path="translations" element={<TranslationsAdmin />} />
              <Route path="pricing" element={<PricingAdmin />} />
              <Route path="press" element={<PressAdmin />} />
            </Route>
          </Routes>
        </SiteProvider>
      </A11yProvider>
    </BrowserRouter>
  );
}

export default App;
